import React, { FC, useState } from 'react';
import {
  CardAssignments,
  ORGANISATION_CARD_PURPOSES,
  OrganisationCardPurpose,
  OrganisationPaymentCard,
  cardBrandLabel,
  cardLabel,
} from './organisationPaymentCards';

interface OrganisationPaymentCardLineProps {
  /** What this line's card pays for */
  purpose: OrganisationCardPurpose;
  cards: OrganisationPaymentCard[];
  assignments: CardAssignments;
  busy: boolean;
  onAssign: (
    purpose: OrganisationCardPurpose,
    cardId: string,
  ) => Promise<boolean>;
  onAddCard: (purposes: OrganisationCardPurpose[]) => void;
}

/**
 * The "Payment method" panel under a billing area: "Visa •••• 1701 · Change
 * payment method". Changing expands into the other saved cards plus "Add a
 * new card". A brand-new card added here is used for this purpose only,
 * unless it is the first card, which pays for everything.
 */
const OrganisationPaymentCardLine: FC<OrganisationPaymentCardLineProps> = ({
  purpose,
  cards,
  assignments,
  busy,
  onAssign,
  onAddCard,
}) => {
  const [changing, setChanging] = useState(false);
  const currentId = assignments[purpose];
  const current = cards.find((c) => c.id === currentId) ?? null;
  const others = cards.filter((c) => c.id !== currentId);

  const addCard = () =>
    onAddCard(cards.length === 0 ? [...ORGANISATION_CARD_PURPOSES] : [purpose]);

  const choose = async (cardId: string) => {
    if (await onAssign(purpose, cardId)) setChanging(false);
  };

  return (
    <div
      className='organisation-payment-panel'
      data-testid={`organisation-card-line-${purpose}`}
    >
      <div className='organisation-payment-row'>
        <span className='organisation-payment-card'>
          {current ? (
            <>
              <span className='organisation-payment-brand' aria-hidden='true'>
                {cardBrandLabel(current)}
              </span>
              <strong>{cardBrandLabel(current)}</strong>
              {current.last4 && (
                <span className='organisation-payment-last4'>
                  •••• {current.last4}
                </span>
              )}
            </>
          ) : (
            <strong>No card saved yet</strong>
          )}
        </span>
        {!changing && (
          <button
            type='button'
            className='btn-link organisation-payment-change'
            disabled={busy}
            onClick={() => (cards.length === 0 ? addCard() : setChanging(true))}
          >
            {cards.length === 0
              ? busy
                ? 'Opening…'
                : 'Add a card'
              : 'Change payment method'}
          </button>
        )}
      </div>
      {changing && (
        <span className='organisation-card-line-choices' role='group'>
          {others.map((card) => (
            <button
              key={card.id}
              type='button'
              className='btn-secondary compact'
              disabled={busy}
              onClick={() => choose(card.id)}
            >
              {cardLabel(card)}
            </button>
          ))}
          <button
            type='button'
            className='btn-secondary compact'
            disabled={busy}
            onClick={addCard}
          >
            {busy ? 'Opening…' : 'Add a new card'}
          </button>
          <button
            type='button'
            className='btn-link'
            disabled={busy}
            onClick={() => setChanging(false)}
          >
            Cancel
          </button>
        </span>
      )}
    </div>
  );
};

export default OrganisationPaymentCardLine;
